import { useMemo } from 'react'
import { Language } from './i18n'
import { translateText } from './translate'

interface TranslatableItem {
  id: string | number
  name: string
  description?: string | null
  [key: string]: any
}

interface TranslatableCategory {
  id: string | number
  name: string
  [key: string]: any
}

/**
 * Hook that returns translated display values for menu items and categories.
 *
 * - For 'es': returns original arrays as-is (zero overhead)
 * - For other languages: returns new arrays with translated name/description
 * - Uses useMemo to avoid re-translating every render
 * - Never mutates original objects
 * - Never throws — falls back to Spanish on any error
 */
export function useTranslatedMenu<
  T extends TranslatableItem,
  C extends TranslatableCategory,
>(
  items: T[],
  categories: C[],
  lang: Language,
): { translatedItems: T[]; translatedCategories: C[] } {
  const translatedItems = useMemo(() => {
    try {
      if (lang === 'es' || !items.length) return items
      return items.map((item) => ({
        ...item,
        name: translateText(item.name, lang),
        description: item.description
          ? translateText(item.description, lang)
          : item.description,
      }))
    } catch {
      return items
    }
  }, [items, lang])

  const translatedCategories = useMemo(() => {
    try {
      if (lang === 'es' || !categories.length) return categories
      return categories.map((cat) => ({
        ...cat,
        name: translateText(cat.name, lang),
      }))
    } catch {
      return categories
    }
  }, [categories, lang])

  return { translatedItems, translatedCategories }
}
